import { useState, useRef, useEffect, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import "./styles.css";

export type FilterDropdownValue = string | number | null;

export interface FilterDropdownOption {
  value: FilterDropdownValue;
  label: string;
}

export interface FilterDropdownProps {
  label?: string;
  value?: FilterDropdownValue;
  options: FilterDropdownOption[];
  onChange: (value: FilterDropdownValue) => void;
  placeholder?: string;
  icon?: ReactNode;
  className?: string;
  buttonClassName?: string;
  menuClassName?: string;
  alignMenu?: "left" | "right";
  isActive?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  menuMinWidth?: number | string;
}

const FilterDropdown = ({
  label,
  value = null,
  options,
  onChange,
  placeholder = "Select an option",
  icon,
  className = "",
  buttonClassName = "",
  menuClassName = "",
  alignMenu = "left",
  isActive,
  fullWidth = false,
  disabled = false,
  menuMinWidth,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const selectedOption = options.find((option) => option.value === value);
  const displayLabel = selectedOption?.label ?? placeholder;
  const computedIsActive =
    typeof isActive === "boolean"
      ? isActive
      : value !== null && value !== undefined && value !== "" && displayLabel !== placeholder;

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (optionValue: FilterDropdownValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdownClasses = [
    "filter-dropdown",
    fullWidth ? "filter-dropdown--full-width" : "",
    computedIsActive ? "filter-dropdown--active" : "",
    disabled ? "filter-dropdown--disabled" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const buttonClasses = ["filter-dropdown__button", buttonClassName].filter(Boolean).join(" ");
  const menuClasses = [
    "filter-dropdown__menu",
    `filter-dropdown__menu--${alignMenu}`,
    menuClassName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={dropdownClasses} ref={dropdownRef}>
      {label && <span className="filter-dropdown__label">{label}</span>}
      <button
        type="button"
        className={buttonClasses}
        onClick={handleToggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        disabled={disabled}
      >
        <span className="filter-dropdown__button-content">
          {icon && <span className="filter-dropdown__icon">{icon}</span>}
          <span className="filter-dropdown__value">{displayLabel}</span>
        </span>
        <ChevronDown size={14} className={`filter-dropdown__chevron ${isOpen ? "open" : ""}`} />
      </button>
      {isOpen && (
        <div className={menuClasses} style={menuMinWidth ? { minWidth: menuMinWidth } : undefined}>
          {options.map((option) => {
            const optionKey = option.value === null ? "null" : String(option.value);
            return (
              <button
                key={optionKey}
                type="button"
                className={`filter-dropdown__menu-item ${option.value === value ? "selected" : ""}`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;

